import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    Card,
    IconButton,
    Chip,
    Avatar,
    Stack,
    TextField,
    InputAdornment,
    LinearProgress,
    Badge,
    Fab,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    Person as PersonIcon,
    Groups as GroupsIcon,
    Business as BusinessIcon,
    ArrowForward as ArrowForwardIcon,
    CalendarToday as CalendarIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as PendingIcon,
    Create as SignIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JHA_STORE, STATUS_NEW, STATUS_PROCESS, WEEK_LIST_STORE, EMPLOYEE_STORE, TEMPS_STORE } from '../../../config/constants';
import { getISOWeek, setWeek, setYear, format } from 'date-fns';
import SelectEmployeesAndTempsModal from '../SelectEmployeesAndTempsModal';
import SelectFromWeeklyRosterModal from '../SelectFromWeeklyRosterModal';
import SafetyAcknowledgmentModal from './SafetyAcknowledgmentModal';
import JhaStepHeader from '../JhaStepHeader';
import { createJhaSkeleton } from '../utils/jhaRecordFactory';
import { generateUUID } from '../../../utils/uuid';
import axiosInstance from '../../../axiosInstance';
import Swal from 'sweetalert2';

export default function JhaCrewStep() {
    const navigate = useNavigate();
    const { state } = useLocation() || {};
    const { isNew = false, jha = null, viewMode = false, fromViewer = false } = state || {};
    const creatingRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [acknowledgments, setAcknowledgments] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [weeklyRosterModalOpen, setWeeklyRosterModalOpen] = useState(false);
    const [acknowledgmentModalOpen, setAcknowledgmentModalOpen] = useState(false);
    const [currentEmployee, setCurrentEmployee] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [jhaData, setJhaData] = useState({
        id: null,
        status: '',
        stepOne: { employeesSelected: [] },
        createdAt: null
    });

    // Email functionality state
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailCooldown, setEmailCooldown] = useState(0);
    const cooldownTimerRef = useRef(null);

    useEffect(() => {
        loadData();
    }, []);

    // Listen for jhaUpdated event to reload data when changes made on other steps
    useEffect(() => {
        const handleJhaUpdated = () => {
            // Reload data from IndexedDB to get latest changes
            loadData();
        };

        window.addEventListener('jhaUpdated', handleJhaUpdated);
        return () => {
            window.removeEventListener('jhaUpdated', handleJhaUpdated);
        };
    }, []);

    // Countdown timer for email cooldown
    useEffect(() => {
        if (emailCooldown > 0) {
            cooldownTimerRef.current = setTimeout(() => {
                setEmailCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => {
            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current);
            }
        };
    }, [emailCooldown]);

    async function loadData() {
        setLoading(true);
        const db = await initializeDB();
        const jhaTx = db.transaction(JHA_STORE, 'readwrite');
        const jhaStore = jhaTx.objectStore(JHA_STORE);
        const allJhaRecords = await jhaStore.getAll();

        if (jha?.id) {
            let existing = await jhaStore.get(jha.id);

            if (existing) {
                const initialStatus = existing.stepThree?.initialSubmission?.status;
                // Allow both 'sent' and 'pending' to access step 4
                if (!viewMode && initialStatus !== 'sent' && initialStatus !== 'pending') {
                    navigate('/jha/step-3', { state: { jha: existing, isNew: false, viewMode } });
                    setLoading(false);
                    return;
                }

                const withMeta = {
                    ...existing,
                    meta: { ...(existing.meta || {}), lastStep: Math.max(existing.meta?.lastStep || 1, 4) }
                };
                await jhaStore.put(withMeta);
                setJhaData(withMeta);
                setSelectedEmployees(withMeta.stepFour.employeesSelected || []);
                setAcknowledgments(withMeta.stepFour.employeeAcknowledgments || []);
            } else {
                const initialStatus = jha.stepThree?.initialSubmission?.status;
                // Allow both 'sent' and 'pending' to access step 4
                if (!viewMode && initialStatus !== 'sent' && initialStatus !== 'pending') {
                    navigate('/jha/step-3', { state: { jha, isNew: false, viewMode } });
                    setLoading(false);
                    return;
                }

                const withMeta = {
                    ...jha,
                    meta: { ...(jha.meta || {}), lastStep: Math.max(jha.meta?.lastStep || 1, 4) }
                };
                await jhaStore.put(withMeta);
                setJhaData(withMeta);
                setSelectedEmployees(withMeta.stepFour.employeesSelected || []);
                setAcknowledgments(withMeta.stepFour.employeeAcknowledgments || []);
            }
        } else if (isNew && !creatingRef.current) {
            creatingRef.current = true;
            const existingNew = allJhaRecords.find(r => r.status === STATUS_NEW);
            if (existingNew) {
                const withMeta = {
                    ...existingNew,
                    meta: { ...(existingNew.meta || {}), lastStep: Math.max(existingNew.meta?.lastStep || 1, 4) }
                };
                await jhaStore.put(withMeta);
                setJhaData(withMeta);
                setSelectedEmployees(withMeta.stepFour.employeesSelected || []);
                setAcknowledgments(withMeta.stepFour.employeeAcknowledgments || []);
            } else {
                const newId = generateUUID();
                const base = createJhaSkeleton({
                    id: newId,
                    status: STATUS_NEW,
                    createdAt: new Date().toISOString(),
                    metaLastStep: 4,
                });
                await jhaStore.put(base);
                setJhaData(base);
                setAcknowledgments([]);
            }
        } else if (!isNew && !jha) {
            // No JHA data and not creating new - redirect to JHA list
            navigate('/jha');
            return;
        }
        setLoading(false);
    }

    async function saveData(updated, minStep = 4) {
        const recordWithMeta = {
            ...updated,
            meta: {
                ...(updated.meta || jhaData?.meta || {}),
                lastStep: Math.max(updated.meta?.lastStep || 0, jhaData?.meta?.lastStep || 0, minStep)
            }
        };

        const db = await initializeDB();
        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        await store.put(recordWithMeta);
        await tx.done;

        window.dispatchEvent(new Event('jhaUpdated'));

        setJhaData(recordWithMeta);
        setSelectedEmployees(recordWithMeta.stepFour.employeesSelected || []);
        setAcknowledgments(recordWithMeta.stepFour.employeeAcknowledgments || []);

        return recordWithMeta;
    }

    // Check if JHA is submitted or in view mode
    const isSubmitted = jhaData && (jhaData.status === 'finish' || jhaData.status === 'completed' || jhaData.syncStatus === 'synced');
    const isReadOnly = isSubmitted || viewMode;

    async function handleSelect(newSelected) {
        if (isReadOnly) return; // Don't allow changes if read-only
        const updated = { ...jhaData };
        updated.stepFour.employeesSelected = newSelected;
        if (newSelected.length && updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        if (!newSelected.length && updated.status === STATUS_PROCESS) {
            updated.status = STATUS_NEW;
        }
        await saveData(updated);
    }

    async function removeSelectedEmployee(employee) {
        if (isReadOnly) return; // Don't allow changes if read-only
        const updated = { ...jhaData };
        const arr = updated.stepFour.employeesSelected.filter(
            emp => !(emp.FullName === employee.FullName && emp._type === employee._type)
        );
        updated.stepFour.employeesSelected = arr;

        // Also remove acknowledgment for this employee
        const updatedAcknowledgments = (updated.stepFour.employeeAcknowledgments || []).filter(
            ack => !(ack.employeeName === employee.FullName && ack._type === employee._type)
        );
        updated.stepFour.employeeAcknowledgments = updatedAcknowledgments;

        if (!arr.length && updated.status === STATUS_PROCESS) {
            updated.status = STATUS_NEW;
        }
        await saveData(updated);
    }

    function getEmployeeStatus(employee) {
        const ack = acknowledgments.find(
            a => a.employeeName === employee.FullName && a._type === employee._type
        );
        if (!ack) return 'pending';
        if (ack.signature && ack.signedAt) return 'signed';
        if (ack.acknowledged) return 'acknowledged';
        return 'pending';
    }

    function handleOpenAcknowledgment(employee) {
        setCurrentEmployee(employee);
        setAcknowledgmentModalOpen(true);
    }

    async function handleSaveAcknowledgment(acknowledgmentData) {
        const updated = { ...jhaData };
        const existingAcknowledgments = updated.stepFour.employeeAcknowledgments || [];

        // Remove existing acknowledgment for this employee if exists
        const filteredAcknowledgments = existingAcknowledgments.filter(
            ack => !(ack.employeeName === acknowledgmentData.employeeName && ack._type === acknowledgmentData._type)
        );

        // Add new acknowledgment
        updated.stepFour.employeeAcknowledgments = [...filteredAcknowledgments, acknowledgmentData];

        await saveData(updated);
    }

    const allEmployeesSigned = selectedEmployees.length > 0 && selectedEmployees.every(emp => {
        return getEmployeeStatus(emp) === 'signed';
    });

    async function handleSendEmail() {
        if (!jhaData?.id || sendingEmail || emailCooldown > 0) return;

        setSendingEmail(true);
        try {
            // Prepare crew members payload from current state
            const crewPayload = selectedEmployees.map(emp => {
                const ack = acknowledgments.find(a =>
                    a.employeeName === emp.FullName && a._type === emp._type
                );
                return {
                    fullName: emp.FullName,
                    signature: ack?.signature || null,
                    _type: emp._type
                };
            });

            // Include safety department data if available
            const payload = {
                crewMembers: crewPayload
            };

            // Add safety department signature if it exists in stepFive
            if (jhaData?.stepFive) {
                if (jhaData.stepFive.safetyEmployee) {
                    payload.safetyEmployee = jhaData.stepFive.safetyEmployee;
                }
                if (jhaData.stepFive.safetySignature) {
                    payload.safetySignature = jhaData.stepFive.safetySignature;
                }
                // Add post-shift checklist data
                payload.postShiftChecklist = {
                    checklist: jhaData.stepFive.checklist || {},
                    checklistNotes: jhaData.stepFive.checklistNotes || {},
                    comments: jhaData.stepFive.comments || '',
                    supervisorSignature: jhaData.stepFive.supervisorSignature || ''
                };
            }

            // Add energy activity rows with hazards (includes custom hazards/actions)
            if (jhaData?.stepThree) {
                const energyRows = jhaData.stepThree.energyActivityRows || jhaData.stepThree.energyExposures || [];
                payload.energyActivityRows = energyRows;
            }

            // Add documents with attachments or linked CSEP from stepTwo
            if (jhaData?.stepTwo) {
                const allDocs = [
                    ...(jhaData.stepTwo.docsPermits || []),
                    ...(jhaData.stepTwo.customDocsPermits || [])
                ];

                // Filter documents that have attachments OR linked CSEP/LTT
                const docsToSend = allDocs
                    .filter(doc => (doc.attachments && doc.attachments.length > 0) || doc.linkedCsepUuid || doc.linkedLttUuid)
                    .map(doc => ({
                        value: doc.value,
                        required: doc.required,
                        attachments: doc.attachments || [],
                        linkedCsepUuid: doc.linkedCsepUuid || null,
                        linkedCsepData: doc.linkedCsepData || null,
                        linkedLttUuid: doc.linkedLttUuid || null,
                        linkedLttData: doc.linkedLttData || null
                    }));

                if (docsToSend.length > 0) {
                    payload.documents = docsToSend;
                }
            }

            const response = await axiosInstance.post(`/jha/local/${jhaData.id}/send-email-with-crew`, payload);

            if (response.data.success) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Email Sent',
                    html: `
                        <div style="text-align: left;">
                            <p>JHA has been sent successfully to:</p>
                            <p><strong>${response.data.sentTo}</strong></p>
                            <p style="color: #666; font-size: 0.9em;">Sent at: ${response.data.sentAt}</p>
                        </div>
                    `,
                    confirmButtonText: 'OK'
                });

                // Start 30 second cooldown
                setEmailCooldown(30);
            }
        } catch (error) {
            console.error('Failed to send email:', error);
            const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Failed to send email';

            await Swal.fire({
                icon: 'error',
                title: 'Email Failed',
                text: errorMessage,
                confirmButtonText: 'OK'
            });
        } finally {
            setSendingEmail(false);
        }
    }

    async function handleNext() {
        if (!selectedEmployees.length || !allEmployeesSigned) return;
        const updated = {
            ...jhaData,
            meta: {
                ...(jhaData?.meta || {}),
                lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
            }
        };
        const saved = await saveData(updated, 5);
        navigate('/jha/step-5', { state: { jha: saved, isNew: false, viewMode } });
    }

    function handleBack() {
        navigate('/jha/step-3', { state: { jha: jhaData, isNew: false, viewMode } });
    }

    // Filter employees based on search
    const filteredEmployees = selectedEmployees.filter(emp => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        const name = (emp.FullName || '').toLowerCase();
        const divisionAgency = ((emp._type === 'employee' ? emp.Division : emp.Agency) || '').toLowerCase();
        return name.includes(search) || divisionAgency.includes(search);
    });

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <LinearProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: 'calc(100vh - 64px)',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            {/* Unified Header */}
            <JhaStepHeader
                currentStep={4}
                jhaData={jhaData}
                fromViewer={fromViewer}
            />

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', minHeight: 0 }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 1200,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        px: { xs: 1, md: 2 },
                        py: 2
                    }}
                >
                <Card sx={{ p: 3, backgroundColor: 'white', flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Read-only alert */}
                {isReadOnly && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This JHA is in read-only mode. You can navigate between steps but cannot make changes.
                    </Alert>
                )}
                
                {/* Selected Count and Add Button */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Badge
                            badgeContent={selectedEmployees.length}
                            color="primary"
                            max={99}
                        >
                            <GroupsIcon sx={{ fontSize: 32, color: '#666' }} />
                        </Badge>
                        <Box>
                            <Typography variant="h6">
                                {selectedEmployees.length} Team {selectedEmployees.length === 1 ? 'Member' : 'Members'}
                            </Typography>
                        </Box>
                    </Box>
                    {!isReadOnly && (
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                startIcon={<CalendarIcon />}
                                onClick={() => setWeeklyRosterModalOpen(true)}
                                sx={{ 
                                    borderColor: '#ff9800',
                                    color: '#ff9800',
                                    '&:hover': { 
                                        borderColor: '#f57c00',
                                        backgroundColor: 'rgba(255, 152, 0, 0.04)'
                                    }
                                }}
                            >
                                From Roster
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setModalOpen(true)}
                                sx={{ 
                                    backgroundColor: '#2196f3',
                                    '&:hover': { backgroundColor: '#1976d2' }
                                }}
                            >
                                Add Members
                            </Button>
                        </Stack>
                    )}
                </Box>

                {/* Search Bar */}
                {selectedEmployees.length > 0 && (
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search selected members..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                {/* Selected Employees List */}
                {selectedEmployees.length === 0 ? (
                    <Card sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        backgroundColor: 'white',
                        border: '2px dashed #e0e0e0'
                    }}>
                        <GroupsIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No team members selected
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {isReadOnly ? 'No employees were selected for this JHA' : 'Select employees from weekly roster or add manually'}
                        </Typography>
                        {!isReadOnly && (
                            <Stack direction="row" spacing={2} justifyContent="center">
                                <Button
                                    variant="outlined"
                                    startIcon={<CalendarIcon />}
                                    onClick={() => setWeeklyRosterModalOpen(true)}
                                >
                                    From Weekly Roster
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setModalOpen(true)}
                                >
                                    Add Members
                                </Button>
                            </Stack>
                        )}
                    </Card>
                ) : (
                    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                        <Stack spacing={1}>
                            {filteredEmployees.map((emp, idx) => {
                                const status = getEmployeeStatus(emp);
                                const statusConfig = {
                                    pending: { color: '#f44336', icon: PendingIcon, label: 'Pending' },
                                    acknowledged: { color: '#ff9800', icon: SignIcon, label: 'Sign' },
                                    signed: { color: '#4caf50', icon: CheckCircleIcon, label: 'Signed' }
                                };
                                const StatusIcon = statusConfig[status].icon;
                                const ack = acknowledgments.find(a => a.employeeName === emp.FullName && a._type === emp._type);
                                const divisionOrAgency = emp._type === 'employee' ? emp.Division : emp.Agency;

                                return (
                                    <Box
                                        key={`member-${emp.FullName}-${emp._type}-${idx}`}
                                        onClick={() => !isReadOnly && handleOpenAcknowledgment(emp)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 0.75,
                                            backgroundColor: 'white',
                                            border: status === 'signed' ? '1px solid #4caf50' : '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            cursor: isReadOnly ? 'default' : 'pointer',
                                            '&:hover': isReadOnly ? {} : { backgroundColor: '#f5f5f5' }
                                        }}
                                    >
                                        <StatusIcon sx={{ fontSize: 16, color: statusConfig[status].color, flexShrink: 0 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {emp.FullName}
                                                {divisionOrAgency && (
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                        ({divisionOrAgency})
                                                    </Typography>
                                                )}
                                            </Typography>
                                            {ack?.signedAt && (
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                    {new Date(ack.signedAt).toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>
                                        {status === 'signed' && ack?.signature && (
                                            <Box
                                                sx={{
                                                    width: 50,
                                                    height: 24,
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 0.5,
                                                    overflow: 'hidden',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <img src={ack.signature} alt="signature" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            </Box>
                                        )}
                                        {!isReadOnly && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeSelectedEmployee(emp);
                                                }}
                                                sx={{ color: '#999', p: 0.25 }}
                                            >
                                                <CloseIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
                </Card>
                </Box>
            </Box>

            {/* Bottom Action */}
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'white',
                    borderTop: '1px solid #e0e0e0',
                    px: 2.5,
                    py: 1.5,
                    zIndex: 10
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Back
                        </Button>

                        {/* Send Email Button - shows when at least 1 employee added */}
                        {selectedEmployees.length > 0 && !isReadOnly && (
                            <Button
                                variant="outlined"
                                color={emailCooldown > 0 ? 'secondary' : 'primary'}
                                startIcon={sendingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
                                onClick={handleSendEmail}
                                disabled={sendingEmail || emailCooldown > 0}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    position: 'relative',
                                    minWidth: emailCooldown > 0 ? 140 : 120
                                }}
                            >
                                {sendingEmail ? 'Sending...' : emailCooldown > 0 ? `Wait ${emailCooldown}s` : 'Send Email'}
                                {emailCooldown > 0 && (
                                    <LinearProgress
                                        variant="determinate"
                                        value={((30 - emailCooldown) / 30) * 100}
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            height: 3,
                                            borderBottomLeftRadius: 4,
                                            borderBottomRightRadius: 4
                                        }}
                                    />
                                )}
                            </Button>
                        )}
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        sx={{ flexWrap: 'nowrap', minWidth: 0, '& > *': { flexShrink: 0 } }}
                    >
                        {selectedEmployees.length > 0 && !allEmployeesSigned && !isReadOnly && (
                            <Chip
                                label="All crew must sign"
                                color="warning"
                                size="small"
                                sx={{ whiteSpace: 'nowrap' }}
                            />
                        )}
                        <Button
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleNext}
                            disabled={!selectedEmployees.length || !allEmployeesSigned}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Continue to Final Review
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            {/* Employee Selection Modal */}
            <SelectEmployeesAndTempsModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                alreadySelected={selectedEmployees}
                onSelect={handleSelect}
            />
            
            {/* Weekly Roster Selection Modal */}
            <SelectFromWeeklyRosterModal
                open={weeklyRosterModalOpen}
                onClose={() => setWeeklyRosterModalOpen(false)}
                onSelect={handleSelect}
            />

            {/* Safety Acknowledgment Modal */}
            <SafetyAcknowledgmentModal
                open={acknowledgmentModalOpen}
                onClose={() => {
                    setAcknowledgmentModalOpen(false);
                    setCurrentEmployee(null);
                }}
                employee={currentEmployee}
                existingAcknowledgment={currentEmployee ? acknowledgments.find(a => a.employeeName === currentEmployee.FullName && a._type === currentEmployee._type) : null}
                hazardsData={jhaData?.stepThree?.energyActivityRows || jhaData?.stepThree?.energyExposures || []}
                onAcknowledge={handleSaveAcknowledgment}
            />
        </Box>
    );
}
